// UI translations for Founder Bottleneck. Diagnostic content (questions,
// dimensions, results copy) comes localized from the backend; this file covers
// static UI chrome, buttons, navigation, validation, feedback and privacy text.

export const translations = {
  es: {
    brandTag: "™",
    nav: { home: "Inicio" },
    privacy: "Tus respuestas se utilizan para generar tu diagnóstico y mejorar la experiencia de Founder Bottleneck.",
    privacyNotice: {
      title: "Tu privacidad importa",
      body: "Founder Bottleneck utiliza tus respuestas para generar tu diagnóstico. No necesitas introducir información confidencial de tu empresa, clientes, empleados o terceros para completar esta evaluación. No compartas información financiera, contractual, personal o propietaria.",
    },
    footer: { privacy: "Política de Privacidad", terms: "Términos de Uso", back: "Volver al inicio" },
    journal: {
      overline: "Journal de 5 días",
      title: "Observa tu cuello de botella en acción",
      intro: "Durante 5 días hábiles, registra lo que realmente ocurre. Sin diarios largos: un número, una nota opcional y si interviniste. Vuelve cada día por este mismo enlace.",
      protocolLabel: "Protocolo",
      dayOf: (n) => `Día ${n} de 5`,
      countLabel: {
        decision: "¿Cuántas decisiones llegaron a ti hoy?",
        approval: "¿Cuántas aprobaciones te solicitaron hoy?",
        problem: "¿Cuántos problemas te escalaron sin una recomendación?",
        information: "¿Cuántas veces te pidieron información que solo tú tenías?",
        operational: "¿Cuántas veces tuviste que intervenir para que algo funcionara?",
        relationship: "¿Cuántas interacciones clave que solo tú podías sostener ocurrieron hoy?",
      },
      countHint: {
        decision: "Cuenta las decisiones que pasaron por ti, aunque pudieran haberse tomado sin ti.",
        approval: "Cuenta cada aprobación que requirió tu visto bueno.",
        problem: "Cuenta los problemas que te llegaron sin opciones ni recomendación.",
        information: "Cuenta las veces que fuiste el único punto de acceso a esa información.",
        operational: "Cuenta las veces que la operación te necesitó para no detenerse.",
        relationship: "Cuenta las relaciones que dependieron de tu presencia directa.",
      },
      notePlaceholder: "¿Qué notaste? (opcional). No incluyas información confidencial.",
      intervenedLabel: "¿Intervine hoy sobre el mecanismo?",
      save: "Guardar día",
      saved: "Guardado",
      update: "Actualizar",
      progress: (n) => `${n} de 5 días registrados`,
      backToResults: "Volver a mi resultado",
      loadError: "No encontramos este journal. Revisa el enlace de tu resultado.",
      measureOverline: "Medición",
      measureTitle: "¿Está cediendo la dependencia?",
      measureBody: "Compara tu primer día con el último. Si el número desciende, el mecanismo empieza a soltar al fundador.",
      metricLabel: "Tu métrica de éxito",
      firstDay: "Primer día",
      lastDay: "Último día",
      interventionsLogged: (n) => `${n} día(s) con intervención marcada`,
      completeBadge: "Journal completado",
      startCta: "Comenzar mi Journal de 5 días",
      startHint: "5 días hábiles. Un minuto al día. Evidencia real de tu avance.",
    },
    legal: {
      reviewNote: "Documento borrador. Debe ser revisado por un profesional jurídico antes del lanzamiento público.",
      privacyPolicy: {
        title: "Política de Privacidad",
        sections: [
          { h: "Introducción", p: ["Esta Política describe cómo Founder Bottleneck™ trata la información cuando completas el diagnóstico. Está redactada para ser clara y precisa; no constituye asesoría legal."] },
          { h: "Qué recopilamos", p: ["Tus respuestas al cuestionario (escala 1–5).", "Indicadores opcionales de línea base que elijas responder.", "El resultado calculado de tu diagnóstico y, si la envías, tu retroalimentación.", "No solicitamos tu nombre ni tu correo electrónico para completar el diagnóstico."] },
          { h: "Qué NO debes introducir", p: ["No introduzcas información confidencial, financiera, contractual, personal, propietaria o secreta de tu empresa, clientes, empleados o terceros. El diagnóstico no la necesita."] },
          { h: "Por qué tratamos estos datos", p: ["Para generar tu diagnóstico, mostrarte tu resultado y mejorar el producto mediante estadísticas agregadas."] },
          { h: "Cómo usamos los datos", p: ["Usamos las respuestas para calcular tu Índice de Dependencia del Fundador™ y el análisis asociado. Los resultados agregados y anónimos nos ayudan a mejorar la herramienta. No vendemos tus datos."] },
          { h: "Retención de datos", p: ["Conservamos los datos de las evaluaciones durante un máximo de 12 meses. Después de ese período, los eliminamos o los anonimizamos según lo que sea técnicamente apropiado."] },
          { h: "Cómo solicitar la eliminación", p: ["Puedes solicitar la eliminación de los datos asociados a tu evaluación escribiendo a pedro@liderazgocreativo.com. Para localizar tu registro necesitaremos información suficiente (por ejemplo, la fecha y el identificador del resultado). Confirmaremos cuando se complete."] },
          { h: "Seguridad", p: ["Aplicamos controles de acceso, transmisión cifrada (HTTPS) y minimización de datos apropiados para esta aplicación. Ningún sistema es completamente seguro, por lo que no ofrecemos garantías absolutas."] },
          { h: "Contacto", p: ["Para cualquier consulta de privacidad: pedro@liderazgocreativo.com."] },
          { h: "Cambios", p: ["Podemos actualizar esta Política. Publicaremos la versión vigente en esta página."] },
        ],
      },
      terms: {
        title: "Términos de Uso",
        sections: [
          { h: "Introducción", p: ["Estos Términos regulan el uso de Founder Bottleneck™. Están redactados de forma clara y no constituyen asesoría legal."] },
          { h: "Naturaleza del servicio", p: ["Founder Bottleneck es una herramienta de diagnóstico que ofrece orientación general basada en tus respuestas. No es asesoría profesional, legal, financiera ni empresarial."] },
          { h: "Uso adecuado", p: ["Te comprometes a usar la herramienta de buena fe y a no intentar vulnerar su seguridad, abusar de sus endpoints ni acceder a datos de otros usuarios."] },
          { h: "No introducir información sensible", p: ["No debes introducir información confidencial, financiera, personal, contractual o propietaria de tu empresa, clientes, empleados o terceros."] },
          { h: "Sin garantías", p: ["El servicio se ofrece “tal cual”. No garantizamos que los resultados sean exactos, completos o adecuados para un fin específico."] },
          { h: "Propiedad", p: ["La metodología, el contenido y la marca Founder Bottleneck™ pertenecen a sus titulares."] },
          { h: "Contacto", p: ["Para consultas sobre estos Términos: pedro@liderazgocreativo.com."] },
          { h: "Cambios", p: ["Podemos actualizar estos Términos. La versión vigente se publicará en esta página."] },
        ],
      },
    },
    landing: {
      overline: "Un diagnóstico para dueños de negocio",
      title: "¿Cuánto sigue tu negocio esperándote a ti?",
      subtitle: "Founder Bottleneck™ revela dónde tu negocio depende demasiado de ti — y qué deberías cambiar a continuación.",
      cta: "Encuentra Mi Cuello de Botella",
      points: [
        "Toma aproximadamente 5–7 minutos",
        "Genera tu Índice de Dependencia del Fundador™",
        "Identifica tu cuello de botella principal",
        "Ofrece un plan de acción práctico de 30 días",
      ],
      promise: [
        "Ve qué está frenando tu negocio.",
        "Entiende cuánto depende de ti.",
        "Sabe qué cambiar a continuación.",
      ],
    },
    diagnostic: {
      sectionOf: (n, total) => `Sección ${n} de ${total}`,
      finalStep: "Paso final",
      dimensionOf: (n, total) => `Dimensión ${n} / ${total}`,
      baselineOverline: "Unos últimos indicadores",
      baselineTitle: "Línea base de impacto",
      baselineSubtitle: "Opcional. Esto no cambia tu puntuación — nos ayuda a medir el cambio real con el tiempo.",
      back: "Atrás",
      continue: "Continuar",
      seeResults: "Ver Mis Resultados",
      answerAll: "Responde todas las preguntas para continuar.",
      loadError: "No se pudo cargar el diagnóstico. Actualiza la página.",
      submitError: "Algo salió mal al calcular tu resultado. Inténtalo de nuevo.",
    },
    results: {
      indexOverline: "Tu Índice de Dependencia del Fundador™",
      of100: "de 100",
      indexHelp: "Puntuaciones más altas significan que tu negocio depende más de ti actualmente.",
      primaryOverline: "Tu cuello de botella principal",
      signalsTitle: "Tus respuestas sugieren",
      whyTitle: "Por qué este es tu cuello de botella",
      symptomLabel: "Síntoma",
      mechanismLabel: "Mecanismo",
      consequenceLabel: "Consecuencia para el negocio",
      strategicMoveOverline: "Tu movimiento estratégico",
      successOverline: "Tu métrica de éxito",
      phases: { observe: "Observa", diagnose: "Diagnostica", intervene: "Interviene", measure: "Mide" },
      planFields: { objective: "Objetivo", action: "Acción", output: "Resultado", metric: "Métrica" },
      contributingNote: (name, score) =>
        `${name} (${score}) está puntuando casi igual de alto y también contribuye. Enfocamos tu plan en tu cuello de botella principal, pero mantén este en la mira.`,
      whatHappening: "Qué está pasando",
      whyMatters: "Por qué importa",
      whatFirst: "Qué cambiar primero",
      fullPictureOverline: "El panorama completo",
      fullPictureTitle: "Dependencia por dimensión",
      primaryTag: "Principal",
      startHereOverline: "Empieza aquí",
      prioritiesTitle: "Tus 3 prioridades",
      planOverline: "Próximos 30 días",
      planTitle: "Tu Plan de 30 Días",
      week: (n) => `Semana ${n}`,
      launch: {
        whatNextOverline: "¿Qué sigue?",
        whatNextBody: "Identificar el cuello de botella es solo el primer paso. Ahora necesitas intervenir sobre el mecanismo que está generando esa dependencia.",
        firstInterventionOverline: "Tu primera intervención",
        q01: "01 — Qué vamos a cambiar",
        q02: "02 — Qué debes observar",
        q03: "03 — Qué debes hacer",
        q04: "04 — Qué vamos a medir",
        centralQuestion: "Pregunta central",
        progressionOverline: "Progresión de 30 días",
        phaseDays: { observe: "Días 1–5", diagnose: "Días 6–10", intervene: "Días 11–20", measure: "Días 21–30" },
        metricClarifier: "Esta métrica no mide si eres un buen fundador. Mide si la dependencia identificada está disminuyendo.",
        signature: "Founder Bottleneck™ · by KOS",
        pdfTagline: "Identifica la dependencia. Cambia el sistema.",
        shareTitle: "Comparte tu primera intervención",
        shareWhatsApp: "Compartir por WhatsApp",
        downloadPdf: "Descargar PDF",
        whatsappHeader: "Hice mi diagnóstico con Founder Bottleneck by KOS. Mi primera intervención:",
        discoverCta: "Descubre tu cuello de botella:",
        reminderTitle: "Añadir recordatorio a mi calendario",
        reminderHint: "Agenda una revisión a 30 días para medir tu avance. No guardamos ningún dato tuyo.",
        reminderEventTitle: "Founder Bottleneck by KOS — Mide tu avance (día 30)",
      },
      interventionPlaybook: {
        decision: { protocol: "Arquitectura de Derechos de Decisión", change: "Rediseñar quién tiene derecho a decidir, no cuánto trabajas tú.", observe: "Durante 5 días hábiles, registra cada decisión que llega a ti y clasifícala por frecuencia, riesgo y reversibilidad.", act: "Elige una categoría de decisiones de bajo riesgo y define quién decide, hasta qué límite y cuándo escalar.", measure: "Número de decisiones que el equipo toma dentro de su autoridad, sin pasar por ti.", question: "¿Qué decisiones sigues tomando tú que otra persona podría tomar con suficiente contexto y autoridad?" },
        approval: { protocol: "Protocolo de Eliminación de Aprobaciones", change: "Eliminar las aprobaciones que ya no requieren tu intervención.", observe: "Durante 5 días hábiles, registra cada aprobación que te soliciten y, para cada una, por qué llegó a ti y qué límite evitaría una mala decisión.", act: "Aplica STOP → PREGUNTA → REDIRIGE a una categoría recurrente: detén la aprobación por costumbre, pregunta qué autoridad debería existir y redirige la decisión con un umbral claro.", measure: "Aprobaciones que requieren tu intervención por semana.", question: "¿Por qué llegó esta aprobación a ti y qué límite permitiría resolverla sin ti?" },
        problem: { protocol: "Protocolo Recomendación-Primero", change: "Dejar de ser el solucionador automático de problemas.", observe: "Durante 5 días hábiles, cada vez que alguien escale un problema, exige: problema, opciones consideradas, recomendación, riesgo principal y qué harían si no estuvieras disponible.", act: "Adopta la regla: no recibas un problema sin recibir también una recomendación.", measure: "Porcentaje de problemas escalados que llegan con una recomendación.", question: "¿Qué harían si tú no estuvieras disponible para resolver esto?" },
        information: { protocol: "Sistema de Independencia de Conocimiento", change: "Reducir la dependencia del conocimiento que solo tú posees.", observe: "Durante 5 días hábiles, registra qué información te solicitaron, quién la necesitaba, por qué no estaba disponible y dónde debería vivir.", act: "Toma la información más solicitada y aplícale: documentar → compartir → delegar → eliminar la dependencia.", measure: "Porcentaje de información crítica accesible sin ti.", question: "¿Dónde debería vivir esta información para que el equipo no dependa de ti?" },
        operational: { protocol: "Prueba de Reemplazo del Fundador", change: "Identificar qué operaciones dejan de funcionar cuando no intervienes.", observe: "Elige una actividad operativa recurrente que hoy dependa de ti y obsérvala durante 5 días hábiles: qué haces exactamente y por qué te necesita.", act: "Documenta para esa actividad: proceso, responsable, criterio de decisión, excepción y mecanismo de escalamiento.", measure: "Capacidad de operar sin el fundador: días que la actividad funciona sin tu intervención directa.", question: "¿Qué tendría que existir para que esta actividad funcione 30 días sin mi intervención directa?" },
        relationship: { protocol: "Sistema de Transferencia de Relaciones", change: "Lograr que las relaciones clave pertenezcan a la empresa, no solo a ti.", observe: "Identifica 3 relaciones estratégicas que hoy dependan directamente de ti y, para cada una, registra su importancia, un contacto secundario y el conocimiento necesario para sostenerla.", act: "Elige la relación de mayor prioridad e introduce un segundo contacto en la próxima interacción que pueda transferirse.", measure: "Porcentaje de relaciones estratégicas gestionables sin tu intervención directa.", question: "¿Esta relación pertenece a la empresa o solo a ti?" },
      },
      retakeText: "Vuelve a hacer el diagnóstico cuando quieras para ver cómo cambia tu dependencia.",
      retakeCta: "Repetir el diagnóstico",
      assist: {
        q: "¿Este diagnóstico confirmó algo que ya sabías, pero no has logrado cambiar?",
        line: "A veces identificar el cuello de botella es solo el primer paso.",
        body: "Si quieres llevar este diagnóstico a una intervención concreta, puedes solicitar una conversación estratégica personal con Pedro Sifontes para trabajar sobre la decisión, estructura o mecanismo que está limitando tu empresa.",
        cta: "Solicitar conversación estratégica →",
        waMessage: "Hola Pedro, acabo de completar mi diagnóstico en Founder Bottleneck by KOS y me gustaría solicitar una conversación estratégica para trabajar sobre mi cuello de botella.",
      },
      finalCta: {
        line1: "Tu cuello de botella es un punto de partida, no la respuesta final.",
        line2: "¿Quieres explorar qué significa esto para tu negocio?",
        button: "Conversemos sobre tu plan →",
      },
      notFound: "No pudimos encontrar ese resultado.",
      startOver: "Empezar de nuevo",
    },
    feedback: {
      overline: "Ayúdanos a mejorar",
      title: "¿Qué tan preciso fue este diagnóstico?",
      commentLabel: "¿Qué parte de tu negocio depende demasiado de ti?",
      commentWarning: "No introduzcas información confidencial, financiera, de empleados, clientes u otra información sensible de tu empresa.",
      optional: "Opcional",
      submit: "Enviar Comentarios",
      thanks: "Gracias. Tus comentarios han sido registrados.",
      success: "Gracias — tus comentarios nos ayudan a mejorar.",
      error: "No se pudieron guardar tus comentarios. Inténtalo de nuevo.",
      questions: [
        {
          id: "accuracy",
          label: "¿Qué tan preciso fue este diagnóstico?",
          options: [
            { value: "very_accurate", label: "Muy preciso" },
            { value: "mostly_accurate", label: "Bastante preciso" },
            { value: "somewhat_accurate", label: "Algo preciso" },
            { value: "not_accurate", label: "No preciso" },
          ],
        },
        {
          id: "identified_real",
          label: "¿Identificó un cuello de botella real en tu negocio?",
          options: [
            { value: "yes", label: "Sí" },
            { value: "partially", label: "Parcialmente" },
            { value: "no", label: "No" },
          ],
        },
        {
          id: "actions_usefulness",
          label: "¿Qué tan útiles son las acciones recomendadas?",
          options: [
            { value: "very_useful", label: "Muy útiles" },
            { value: "useful", label: "Útiles" },
            { value: "somewhat_useful", label: "Algo útiles" },
            { value: "not_useful", label: "No útiles" },
          ],
        },
      ],
    },
  },

  en: {
    brandTag: "™",
    nav: { home: "Home" },
    privacy: "Your responses are used to generate your diagnosis and improve the Founder Bottleneck experience.",
    privacyNotice: {
      title: "Your privacy matters",
      body: "Founder Bottleneck uses your answers to generate your diagnosis. You do not need to enter confidential information about your company, clients, employees or third parties to complete this assessment. Do not share financial, contractual, personal or proprietary information.",
    },
    footer: { privacy: "Privacy Policy", terms: "Terms of Use", back: "Back to home" },
    journal: {
      overline: "5-Day Journal",
      title: "Watch your bottleneck in action",
      intro: "For 5 business days, record what actually happens. No long diaries: a number, an optional note, and whether you intervened. Come back each day using this same link.",
      protocolLabel: "Protocol",
      dayOf: (n) => `Day ${n} of 5`,
      countLabel: {
        decision: "How many decisions reached you today?",
        approval: "How many approvals were requested of you today?",
        problem: "How many problems were escalated to you without a recommendation?",
        information: "How many times were you asked for information only you had?",
        operational: "How many times did you have to step in to keep something working?",
        relationship: "How many key interactions that only you could sustain happened today?",
      },
      countHint: {
        decision: "Count the decisions that routed through you, even if they could have been made without you.",
        approval: "Count every approval that needed your sign-off.",
        problem: "Count the problems that reached you with no options or recommendation.",
        information: "Count the times you were the only access point to that information.",
        operational: "Count the times the operation needed you to keep running.",
        relationship: "Count the relationships that depended on your direct presence.",
      },
      notePlaceholder: "What did you notice? (optional). Do not include confidential information.",
      intervenedLabel: "Did I intervene on the mechanism today?",
      save: "Save day",
      saved: "Saved",
      update: "Update",
      progress: (n) => `${n} of 5 days logged`,
      backToResults: "Back to my result",
      loadError: "We couldn't find this journal. Check your result link.",
      measureOverline: "Measurement",
      measureTitle: "Is the dependency easing?",
      measureBody: "Compare your first day with your last. If the number drops, the mechanism is starting to release the founder.",
      metricLabel: "Your success metric",
      firstDay: "First day",
      lastDay: "Last day",
      interventionsLogged: (n) => `${n} day(s) marked with an intervention`,
      completeBadge: "Journal completed",
      startCta: "Start my 5-Day Journal",
      startHint: "5 business days. One minute a day. Real evidence of your progress.",
    },
    legal: {
      reviewNote: "Draft document. It should be reviewed by a legal professional before public launch.",
      privacyPolicy: {
        title: "Privacy Policy",
        sections: [
          { h: "Introduction", p: ["This Policy describes how Founder Bottleneck™ handles information when you complete the diagnostic. It is written to be clear and precise; it is not legal advice."] },
          { h: "What we collect", p: ["Your questionnaire answers (1–5 scale).", "Optional baseline indicators you choose to answer.", "Your calculated diagnostic result and, if you submit it, your feedback.", "We do not ask for your name or email to complete the diagnostic."] },
          { h: "What you should NOT enter", p: ["Do not enter confidential, financial, contractual, personal, proprietary or secret information about your company, clients, employees or third parties. The diagnostic does not need it."] },
          { h: "Why we process this data", p: ["To generate your diagnosis, show you your result, and improve the product through aggregated statistics."] },
          { h: "How we use the data", p: ["We use your answers to compute your Founder Dependency Index™ and the related analysis. Aggregated, anonymous results help us improve the tool. We do not sell your data."] },
          { h: "Data retention", p: ["We keep assessment data for a maximum of 12 months. After that period we delete or anonymize it, whichever is technically appropriate."] },
          { h: "How to request deletion", p: ["You can request deletion of the data associated with your assessment by writing to pedro@liderazgocreativo.com. To locate your record we will need enough information (for example, the date and the result identifier). We will confirm once it is done."] },
          { h: "Security", p: ["We apply access controls, encrypted transmission (HTTPS) and data minimization appropriate for this application. No system is completely secure, so we do not make absolute guarantees."] },
          { h: "Contact", p: ["For any privacy question: pedro@liderazgocreativo.com."] },
          { h: "Changes", p: ["We may update this Policy. The current version will be posted on this page."] },
        ],
      },
      terms: {
        title: "Terms of Use",
        sections: [
          { h: "Introduction", p: ["These Terms govern the use of Founder Bottleneck™. They are written to be clear and are not legal advice."] },
          { h: "Nature of the service", p: ["Founder Bottleneck is a diagnostic tool that provides general guidance based on your answers. It is not professional, legal, financial or business advice."] },
          { h: "Acceptable use", p: ["You agree to use the tool in good faith and not to attempt to breach its security, abuse its endpoints, or access other users' data."] },
          { h: "Do not enter sensitive information", p: ["You must not enter confidential, financial, personal, contractual or proprietary information about your company, clients, employees or third parties."] },
          { h: "No warranties", p: ["The service is provided “as is.” We do not warrant that results are accurate, complete or fit for a particular purpose."] },
          { h: "Ownership", p: ["The Founder Bottleneck™ methodology, content and brand belong to their owners."] },
          { h: "Contact", p: ["For questions about these Terms: pedro@liderazgocreativo.com."] },
          { h: "Changes", p: ["We may update these Terms. The current version will be posted on this page."] },
        ],
      },
    },
    landing: {
      overline: "A diagnostic for business owners",
      title: "How much is your business still waiting for you?",
      subtitle: "Founder Bottleneck™ reveals where your business depends too heavily on you — and what you should change next.",
      cta: "Find My Bottleneck",
      points: [
        "Takes approximately 5–7 minutes",
        "Produces your Founder Dependency Index™",
        "Identifies your primary bottleneck",
        "Provides a practical 30-day action plan",
      ],
      promise: [
        "See what is holding your business back.",
        "Understand how much depends on you.",
        "Know what to change next.",
      ],
    },
    diagnostic: {
      sectionOf: (n, total) => `Section ${n} of ${total}`,
      finalStep: "Final step",
      dimensionOf: (n, total) => `Dimension ${n} / ${total}`,
      baselineOverline: "A few final indicators",
      baselineTitle: "Impact baseline",
      baselineSubtitle: "Optional. These do not change your score — they help us measure real change over time.",
      back: "Back",
      continue: "Continue",
      seeResults: "See My Results",
      answerAll: "Please answer every question to continue.",
      loadError: "Could not load the diagnostic. Please refresh.",
      submitError: "Something went wrong calculating your result. Please try again.",
    },
    results: {
      indexOverline: "Your Founder Dependency Index™",
      of100: "of 100",
      indexHelp: "Higher scores mean your business currently depends more heavily on you.",
      primaryOverline: "Your primary bottleneck",
      signalsTitle: "Your answers suggest",
      whyTitle: "Why this is your bottleneck",
      symptomLabel: "Symptom",
      mechanismLabel: "Mechanism",
      consequenceLabel: "Business consequence",
      strategicMoveOverline: "Your strategic move",
      successOverline: "Your success metric",
      phases: { observe: "Observe", diagnose: "Diagnose", intervene: "Intervene", measure: "Measure" },
      planFields: { objective: "Objective", action: "Action", output: "Output", metric: "Metric" },
      contributingNote: (name, score) =>
        `${name} (${score}) is scoring almost as high and is also contributing. We've focused your plan on your single primary bottleneck, but keep this one in view.`,
      whatHappening: "What is happening",
      whyMatters: "Why it matters",
      whatFirst: "What to change first",
      fullPictureOverline: "The full picture",
      fullPictureTitle: "Dependency by dimension",
      primaryTag: "Primary",
      startHereOverline: "Start here",
      prioritiesTitle: "Your 3 priorities",
      planOverline: "Next 30 days",
      planTitle: "Your 30-Day Bottleneck Plan",
      week: (n) => `Week ${n}`,
      launch: {
        whatNextOverline: "What's next?",
        whatNextBody: "Identifying the bottleneck is only the first step. Now you need to intervene on the mechanism creating that dependency.",
        firstInterventionOverline: "Your first intervention",
        q01: "01 — What we'll change",
        q02: "02 — What to observe",
        q03: "03 — What to do",
        q04: "04 — What we'll measure",
        centralQuestion: "Core question",
        progressionOverline: "30-day progression",
        phaseDays: { observe: "Days 1–5", diagnose: "Days 6–10", intervene: "Days 11–20", measure: "Days 21–30" },
        metricClarifier: "This metric does not measure whether you're a good founder. It measures whether the identified dependency is decreasing.",
        signature: "Founder Bottleneck™ · by KOS",
        pdfTagline: "Identify the dependency. Change the system.",
        shareTitle: "Share your first intervention",
        shareWhatsApp: "Share on WhatsApp",
        downloadPdf: "Download PDF",
        whatsappHeader: "I ran my diagnostic with Founder Bottleneck by KOS. My first intervention:",
        discoverCta: "Find your bottleneck:",
        reminderTitle: "Add reminder to my calendar",
        reminderHint: "Schedule a 30-day check-in to measure your progress. We don't store any data about you.",
        reminderEventTitle: "Founder Bottleneck by KOS — Measure your progress (day 30)",
      },
      interventionPlaybook: {
        decision: { protocol: "Decision Rights Architecture", change: "Redesign who has the right to decide — not how much you work.", observe: "For 5 business days, log every decision that reaches you and classify it by frequency, risk and reversibility.", act: "Pick one low-risk decision category and define who decides, up to what limit, and when to escalate.", measure: "Number of decisions the team makes within their authority, without routing to you.", question: "Which decisions are you still making that someone else could make with enough context and authority?" },
        approval: { protocol: "Approval Elimination Protocol", change: "Eliminate approvals that no longer require your intervention.", observe: "For 5 business days, record every approval requested of you and, for each, why it reached you and what limit would prevent a bad decision.", act: "Apply STOP → ASK → REDIRECT to one recurring category: stop approving out of habit, ask what authority should exist, and redirect the decision with a clear threshold.", measure: "Approvals requiring your intervention per week.", question: "Why did this approval reach you, and what limit would let it be resolved without you?" },
        problem: { protocol: "Recommendation-First Protocol", change: "Stop being the automatic problem-solver.", observe: "For 5 business days, whenever someone escalates a problem, require: the problem, options considered, a recommendation, the main risk, and what they'd do if you were unavailable.", act: "Adopt the rule: never take a problem without taking a recommendation.", measure: "Percentage of escalated problems that arrive with a recommendation.", question: "What would they do if you weren't available to resolve this?" },
        information: { protocol: "Knowledge Independence System", change: "Reduce dependency on knowledge only you hold.", observe: "For 5 business days, record what information was requested of you, who needed it, why it wasn't available, and where it should live.", act: "Take the most-requested information and apply: document → share → delegate → remove the dependency.", measure: "Percentage of critical information accessible without you.", question: "Where should this information live so the team doesn't depend on you?" },
        operational: { protocol: "Founder Replacement Test", change: "Identify which operations stop working when you don't step in.", observe: "Pick one recurring operational activity that depends on you today and observe it for 5 business days: what exactly you do and why it needs you.", act: "Document for that activity: the process, the owner, the decision criteria, the exceptions and an escalation mechanism.", measure: "Founder-free operating capacity: days the activity runs without your direct involvement.", question: "What would need to exist for this activity to run 30 days without my direct involvement?" },
        relationship: { protocol: "Relationship Transfer System", change: "Make key relationships belong to the company, not only to you.", observe: "Identify 3 strategic relationships that depend directly on you today and, for each, note its importance, a secondary contact, and the knowledge needed to sustain it.", act: "Pick the highest-priority relationship and introduce a second contact into the next interaction that can be transferred.", measure: "Percentage of strategic relationships manageable without your direct involvement.", question: "Does this relationship belong to the company, or only to you?" },
      },
      retakeText: "Retake the diagnostic anytime to see how your dependency changes.",
      retakeCta: "Retake the diagnostic",
      assist: {
        q: "Did this diagnosis confirm something you already knew, but haven't been able to change?",
        line: "Sometimes identifying the bottleneck is only the first step.",
        body: "If you want to turn this diagnosis into a concrete intervention, you can request a personal strategic conversation with Pedro Sifontes to work on the decision, structure, or mechanism limiting your business.",
        cta: "Request a strategic conversation →",
        waMessage: "Hi Pedro, I just completed my diagnosis in Founder Bottleneck by KOS and I'd like to request a strategic conversation to work on my bottleneck.",
      },
      finalCta: {
        line1: "Your bottleneck is a starting point, not the final answer.",
        line2: "Want to explore what this means for your business?",
        button: "Discuss your roadmap →",
      },
      notFound: "We couldn't find that result.",
      startOver: "Start over",
    },
    feedback: {
      overline: "Help us improve",
      title: "How accurate was this diagnosis?",
      commentLabel: "What part of your business still depends too much on you?",
      commentWarning: "Please do not enter confidential, financial, employee, customer, or other sensitive business information in this assessment.",
      optional: "Optional",
      submit: "Submit Feedback",
      thanks: "Thank you. Your feedback has been recorded.",
      success: "Thank you — your feedback helps us improve.",
      error: "Could not save your feedback. Please try again.",
      questions: [
        {
          id: "accuracy",
          label: "How accurate was this diagnosis?",
          options: [
            { value: "very_accurate", label: "Very accurate" },
            { value: "mostly_accurate", label: "Mostly accurate" },
            { value: "somewhat_accurate", label: "Somewhat accurate" },
            { value: "not_accurate", label: "Not accurate" },
          ],
        },
        {
          id: "identified_real",
          label: "Did this identify a real bottleneck in your business?",
          options: [
            { value: "yes", label: "Yes" },
            { value: "partially", label: "Partially" },
            { value: "no", label: "No" },
          ],
        },
        {
          id: "actions_usefulness",
          label: "How useful are the recommended actions?",
          options: [
            { value: "very_useful", label: "Very useful" },
            { value: "useful", label: "Useful" },
            { value: "somewhat_useful", label: "Somewhat useful" },
            { value: "not_useful", label: "Not useful" },
          ],
        },
      ],
    },
  },
};
